import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
    ChevronRight, 
    AlertTriangle,
    ArrowLeft,
    Copy,
    Check,
    Eye
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Code block component (same as DocsPage)
function CodeBlock({ children, className, ...props }) {
    const [copied, setCopied] = useState(false);
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : '';
    const code = String(children).replace(/\n$/, '');

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!match) {
        return <code className={cn("bg-muted px-1.5 py-0.5 rounded text-sm font-mono", className)} {...props}>{children}</code>;
    }

    return (
        <div className="relative group my-4">
            <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                <Badge variant="outline" className="text-xs font-mono bg-background/80">
                    {language}
                </Badge>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity bg-background/80"
                    onClick={handleCopy}
                >
                    {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                </Button>
            </div>
            <SyntaxHighlighter
                style={oneDark}
                language={language}
                PreTag="div"
                customStyle={{
                    margin: 0,
                    borderRadius: '0.5rem',
                    fontSize: '0.875rem',
                }}
            >
                {code}
            </SyntaxHighlighter>
        </div>
    );
}

export default function DraftPreview() {
    const { docId } = useParams();
    const navigate = useNavigate();
    const { user, token, loading: authLoading } = useAuth();
    const [doc, setDoc] = useState(null);
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!authLoading && (!user || !user.is_admin)) {
            navigate('/login');
        }
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (user?.is_admin && docId) {
            fetchPreview();
        }
    }, [user, docId]);

    const fetchPreview = async () => {
        try {
            const response = await axios.get(`${API}/docs/preview/${docId}`);
            setDoc(response.data);
            
            // Fetch project info
            if (response.data.project_id) {
                const projectRes = await axios.get(`${API}/projects`);
                const proj = projectRes.data.find(p => p.id === response.data.project_id);
                setProject(proj);
            }
        } catch (err) {
            console.error('Error fetching preview:', err);
            setError(err.response?.data?.detail || 'Fehler beim Laden der Vorschau');
        } finally {
            setLoading(false);
        }
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse text-primary font-mono">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-background">
                <Navbar />
                <div className="pt-32 text-center">
                    <AlertTriangle className="w-16 h-16 text-destructive mx-auto mb-4" />
                    <h1 className="text-2xl font-mono font-bold">{error}</h1>
                    <Link to="/admin" className="text-primary hover:underline mt-4 inline-block">
                        Zurück zum Admin
                    </Link>
                </div>
            </div>
        );
    }

    if (!doc) {
        return null;
    }

    return (
        <div className="min-h-screen bg-background">
            <div className="noise-overlay" />
            <Navbar />

            <div className="pt-24 px-6 lg:px-12 py-8">
                {/* Preview Banner */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="max-w-3xl mx-auto mb-6 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30"
                >
                    <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-yellow-500" />
                        <div>
                            <h3 className="font-mono font-bold text-yellow-500">Vorschau-Modus</h3>
                            <p className="text-sm text-muted-foreground">
                                Dies ist eine Admin-Vorschau. Das Dokument ist {doc.is_published ? 'veröffentlicht' : 'noch nicht veröffentlicht'}.
                            </p>
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="ml-auto"
                            onClick={() => navigate('/admin')}
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Zurück
                        </Button>
                    </div>
                </motion.div>

                {/* Document Content */}
                <motion.article
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="max-w-3xl mx-auto"
                >
                    {/* Breadcrumb */}
                    <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                        <Link to="/" className="hover:text-foreground transition-colors">
                            Home
                        </Link>
                        <ChevronRight className="w-4 h-4" />
                        {project && (
                            <>
                                <span className="text-foreground">{project.name}</span>
                                <ChevronRight className="w-4 h-4" />
                            </>
                        )}
                        <span className="text-foreground">{doc.title}</span>
                    </nav>

                    {/* Header */}
                    <header className="mb-8">
                        <div className="flex items-center gap-2 mb-4 flex-wrap">
                            <Badge variant="outline" className="font-mono text-xs">
                                {doc.category}
                            </Badge>
                            {!doc.is_published && (
                                <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-500">
                                    Entwurf
                                </Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                                {doc.language === 'en' ? 'EN' : 'DE'}
                            </Badge>
                        </div>
                        <h1 className="text-4xl font-mono font-bold mb-4">
                            {doc.title}
                        </h1>
                        <div className="text-sm text-muted-foreground">
                            Slug: <code className="bg-muted px-2 py-0.5 rounded">{doc.slug}</code>
                        </div>
                    </header>

                    {/* Content */}
                    <div className="prose prose-invert max-w-none">
                        <ReactMarkdown
                            components={{
                                code: CodeBlock,
                                h1: ({ children }) => (
                                    <h1 className="text-3xl font-mono font-bold mt-8 mb-4">{children}</h1>
                                ),
                                h2: ({ children }) => (
                                    <h2 className="text-2xl font-mono font-bold mt-8 mb-4 pb-2 border-b border-border">{children}</h2>
                                ),
                                h3: ({ children }) => (
                                    <h3 className="text-xl font-mono font-semibold mt-6 mb-3">{children}</h3>
                                ),
                                p: ({ children }) => (
                                    <p className="text-muted-foreground leading-relaxed mb-4">{children}</p>
                                ),
                                ul: ({ children }) => (
                                    <ul className="list-disc list-inside space-y-2 mb-4 text-muted-foreground">{children}</ul>
                                ),
                                ol: ({ children }) => (
                                    <ol className="list-decimal list-inside space-y-2 mb-4 text-muted-foreground">{children}</ol>
                                ),
                                a: ({ href, children }) => (
                                    <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                                        {children}
                                    </a>
                                ),
                                blockquote: ({ children }) => (
                                    <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground my-4">
                                        {children}
                                    </blockquote>
                                ),
                                img: ({ src, alt }) => (
                                    <img 
                                        src={src} 
                                        alt={alt} 
                                        className="rounded-lg border border-border my-4 max-w-full"
                                    />
                                ),
                            }}
                        >
                            {doc.content.replace(/\n/g, '  \n')}
                        </ReactMarkdown>
                    </div>
                </motion.article>
            </div>
        </div>
    );
}
